/* Cart drawer — AJAX cart with slide-in panel.
   Uses the Shopify Cart API + Section Rendering API to update without reloads. */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.panel = this.querySelector('.cart-drawer__panel');
    this.body = this.querySelector('[data-cart-body]');

    this.querySelectorAll('[data-cart-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
    this.bindBody();
  }

  /* Re-bind item-level controls after each re-render */
  bindBody() {
    this.querySelectorAll('[data-cart-remove]').forEach((btn) =>
      btn.addEventListener('click', () => this.changeLine(parseInt(btn.dataset.cartRemove, 10), 0))
    );
    this.querySelectorAll('quantity-stepper').forEach((stepper) => {
      const input = stepper.querySelector('[data-qty-input]');
      const line = parseInt(stepper.dataset.line, 10);
      stepper.querySelector('[data-qty-minus]').addEventListener('click', () =>
        this.changeLine(line, Math.max(0, parseInt(input.value, 10) - 1))
      );
      stepper.querySelector('[data-qty-plus]').addEventListener('click', () =>
        this.changeLine(line, parseInt(input.value, 10) + 1)
      );
      input.addEventListener('change', () =>
        this.changeLine(line, Math.max(0, parseInt(input.value, 10) || 0))
      );
    });
    const note = this.querySelector('[data-cart-note]');
    if (note) {
      note.addEventListener('change', () => this.updateNote(note.value));
    }
    const checkout = this.querySelector('[data-cart-checkout]');
    if (checkout) {
      checkout.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = '/checkout';
      });
    }
  }

  open() {
    this.hidden = false;
    requestAnimationFrame(() => this.setAttribute('data-open', ''));
    document.documentElement.style.overflow = 'hidden';
    const close = this.querySelector('.cart-drawer__close');
    if (close) close.focus();
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  close() {
    this.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
    setTimeout(() => { this.hidden = true; }, 320);
  }

  async changeLine(line, quantity) {
    this.setLoading(true);
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer-contents'], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      this.renderContents(data);
      this.updateCount(data.item_count);
    } catch (e) {
      console.error('Cart update failed', e);
    } finally {
      this.setLoading(false);
    }
  }

  async updateNote(note) {
    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch (e) { /* non-blocking */ }
  }

  /* Called by product forms after a successful add */
  async refresh(openAfter = true) {
    try {
      const res = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer-contents`);
      const html = await res.text();
      this.replaceContents(html);
      const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartRes.json();
      this.updateCount(cart.item_count);
      if (openAfter) this.open();
    } catch (e) {
      console.error('Cart refresh failed', e);
    }
  }

  renderContents(changeResponse) {
    const html = changeResponse.sections && changeResponse.sections['cart-drawer-contents'];
    if (html) this.replaceContents(html);
    if (changeResponse.item_count === 0) this.setAttribute('data-empty', '');
    else this.removeAttribute('data-empty');
  }

  replaceContents(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector('[data-cart-body]') || tmp;
    this.body.innerHTML = fresh.innerHTML;
    this.bindBody();
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('header__cart-count--empty', count === 0);
    });
    const label = this.querySelector('[data-cart-count-label]');
    if (label) label.textContent = `(${count})`;
  }

  setLoading(state) {
    this.classList.toggle('cart-drawer--loading', state);
  }
}
customElements.define('cart-drawer', CartDrawer);

/* Open the drawer from any header cart trigger */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-cart-trigger]');
  if (!trigger) return;
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    e.preventDefault();
    drawer.open();
  }
});
